import React from 'react';

import { queryAsset } from './asset-selector-utils';
import { ApiType } from '../../api/api';
import { _ } from '../../libs';
import * as types from '../../types';

type SharedLoaderContextValue = {
  loadFids: (_fids: string[], _callback: (_results: types.assetSelector.AssetSelectorMetaData[]) => void) => void;
};

const AssetSelectorSharedLoaderContext = React.createContext<SharedLoaderContextValue | null>(null);

type AssetSelectorSharedLoaderProviderProps = {
  children: React.ReactNode;
  assetMasterQueryApi?: ApiType['assetMaster']['assetMaster'];
};

type PendingRequest = {
  fids: string[];
  callbacks: Set<(_results: types.assetSelector.AssetSelectorMetaData[]) => void>;
};

/**
 * Provider that batches initial asset metadata requests from multiple AssetSelector components.
 * When multiple selectors request assets, they are collected and fetched in a single API call.
 */
export const AssetSelectorSharedLoaderProvider = ({
  children,
  assetMasterQueryApi,
}: AssetSelectorSharedLoaderProviderProps) => {
  // cache of already loaded assets, keyed by fid (uses object instead of Map)
  const cacheRef = React.useRef<{ [fid: string]: types.assetSelector.AssetSelectorMetaData }>({});
  // pending requests waiting to be batched, keyed by sorted comma-separated fids (object instead of Map)
  const pendingRequestsRef = React.useRef<{ [requestKey: string]: PendingRequest }>({});
  // flag to prevent concurrent batch processing
  const isBatchInProgressRef = React.useRef(false);

  /**
   * Processes all pending requests by collecting unique fids and making a single API call.
   * Only requests fids that aren't already in the cache.
   */
  const processBatch = React.useCallback(async () => {
    // prevent concurrent batch processing
    if (isBatchInProgressRef.current) return;

    const pending = pendingRequestsRef.current;
    const pendingKeys = Object.keys(pending);
    if (pendingKeys.length === 0) return;

    isBatchInProgressRef.current = true;

    // Collect all unique fids from all pending requests, only include fids that aren't already cached
    const allPendingFids = _.uniq(_.flatMap(pendingKeys, (requestKey) => pending[requestKey].fids));
    const fidsToRequest: string[] = allPendingFids.filter((fid) => !(fid in cacheRef.current));

    // if all requested fids are already cached, return cached results immediately
    if (_.isEmpty(fidsToRequest)) {
      pendingKeys.forEach((requestKey) => {
        const request = pending[requestKey];
        const results = request.fids
          .map((fid) => cacheRef.current[fid])
          .filter((asset): asset is types.assetSelector.AssetSelectorMetaData => asset !== undefined);
        request.callbacks.forEach((callback) => callback(results));
      });
      // clear
      pendingKeys.forEach((key) => {
        delete pending[key];
      });
      isBatchInProgressRef.current = false;
      return;
    }

    try {
      // make a single API call with all unique uncached fids
      const queryResult = await queryAsset(
        {
          search_text: '',
          fids: fidsToRequest,
        },
        fidsToRequest.length,
        0,
        undefined,
        assetMasterQueryApi
      );

      // store fetched assets in cache
      if (!queryResult.error && queryResult.response?.assets) {
        queryResult.response.assets.forEach((asset: types.assetSelector.AssetSelectorMetaData) => {
          if (asset.fid) {
            cacheRef.current[asset.fid] = asset;
          }
        });
      }

      // send results to all pending request callbacks
      pendingKeys.forEach((requestKey) => {
        const request = pending[requestKey];
        const results = request.fids
          .map((fid) => cacheRef.current[fid])
          .filter((asset): asset is types.assetSelector.AssetSelectorMetaData => asset !== undefined);
        request.callbacks.forEach((callback) => callback(results));
      });
    } catch (error) {
      // on error, call all callbacks with empty results
      pendingKeys.forEach((requestKey) => {
        const request = pending[requestKey];
        request.callbacks.forEach((callback) => callback([]));
      });
    } finally {
      // clear pending requests and reset batch flag
      pendingKeys.forEach((key) => {
        delete pending[key];
      });
      isBatchInProgressRef.current = false;
    }
  }, []);

  /**
   * Schedules a batch request using debounce.
   */
  const debouncedProcessBatch = React.useMemo(
    () =>
      _.debounce(() => {
        processBatch();
      }, 75),
    [processBatch]
  );

  const scheduleBatch = React.useCallback(() => {
    debouncedProcessBatch();
  }, [debouncedProcessBatch]);

  /**
   * Main entry point for loading asset metadata.
   * Checks cache first, then registers for batch processing if needed.
   */
  const loadFids = React.useCallback(
    (fids: string[], callback: (_results: types.assetSelector.AssetSelectorMetaData[]) => void) => {
      // handle empty input
      if (_.isEmpty(fids)) {
        callback([]);
        return;
      }

      // check if all requested fids are already in cache
      const cachedResults = fids
        .map((fid) => cacheRef.current[fid])
        .filter((asset): asset is types.assetSelector.AssetSelectorMetaData => asset !== undefined);

      // if all fids are cached, return immediately without batching
      if (cachedResults.length === fids.length) {
        callback(cachedResults);
        return;
      }

      // group requests by the same set of fids (using sorted fids as key)
      // multiple selectors requesting the same fids will share the same request entry
      const requestKey = fids.slice().sort().join(',');
      let request = pendingRequestsRef.current[requestKey];

      // create new request entry if this is the first selector requesting these fids
      if (!request) {
        request = {
          fids: [...fids],
          callbacks: new Set(),
        };
        pendingRequestsRef.current[requestKey] = request;
      }

      // add this selectors callback to the request
      request.callbacks.add(callback);
      // schedule batch processing (will debounce if called multiple times)
      scheduleBatch();
    },
    [scheduleBatch]
  );

  React.useEffect(() => {
    // On unmount, cancel the debounced function if present
    return () => {
      debouncedProcessBatch.cancel();
    };
  }, [debouncedProcessBatch]);

  const contextValue = React.useMemo(
    () => ({
      loadFids,
    }),
    [loadFids]
  );

  return (
    <AssetSelectorSharedLoaderContext.Provider value={contextValue}>
      {children}
    </AssetSelectorSharedLoaderContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAssetSelectorSharedLoader = () => {
  const context = React.useContext(AssetSelectorSharedLoaderContext);
  return context;
};
